// src/api/creditoApp.js - API para reportes semanales de apps de crédito (Cashea, etc.)
import { apiFetch } from './index.js';

/**
 * Formatea una fecha Date a 'YYYY-MM-DD' (formato que espera el backend)
 * @param {Date|string} fecha
 * @returns {string|null}
 */
const formatFecha = (fecha) => {
  if (!fecha) return null;
  if (typeof fecha === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return fecha;
    return fecha.split('T')[0];
  }
  const d = new Date(fecha);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Calcula el primer miércoles ESTRICTAMENTE MAYOR a la fecha dada.
 * Misma lógica del backend (CalcularMiercolesPago).
 * 
 * @param {Date|string} fechaGeneracion
 * @returns {Date}
 */
export const calcularMiercolesPago = (fechaGeneracion) => {
  const fecha = new Date(fechaGeneracion);
  const diaSemana = fecha.getDay(); // Dom=0, Lun=1, ..., Mié=3
  let diasParaMiercoles = (3 - diaSemana + 7) % 7;
  if (diasParaMiercoles === 0) diasParaMiercoles = 7;
  const resultado = new Date(fecha);
  resultado.setDate(resultado.getDate() + diasParaMiercoles);
  resultado.setHours(0, 0, 0, 0);
  return resultado;
};

/**
 * Obtiene el próximo miércoles de pago (a partir de hoy).
 * @returns {Date}
 */
export const obtenerProximoMiercoles = () => {
  return calcularMiercolesPago(new Date());
};

// ============================================================
//  ENDPOINTS
// ============================================================

/**
 * Reporte del pago de una app (ej. Cashea) para un miércoles específico.
 * GET /api/credito-app/pago-semanal?codigoTipoCredito=CAS-001&fecha=2026-04-29
 * 
 * @param {string} codigoTipoCredito - Ej: 'CAS-001'
 * @param {Date|string} fecha - Fecha del miércoles (YYYY-MM-DD o Date)
 * @returns {Promise}
 */
export const obtenerPagoSemanal = async (codigoTipoCredito, fecha) => {
  try {
    if (!codigoTipoCredito) {
      throw new Error('Código de tipo de crédito es requerido');
    }

    const fechaStr = formatFecha(fecha);
    if (!fechaStr) {
      throw new Error('Fecha es requerida');
    }

    console.log(`📊 Obteniendo pago semanal ${codigoTipoCredito} para ${fechaStr}...`);

    const response = await apiFetch(
      `/api/credito-app/pago-semanal?codigoTipoCredito=${encodeURIComponent(codigoTipoCredito)}&fecha=${fechaStr}`
    );

    return {
      success: true,
      data: response.data || response,
      message: 'Pago semanal obtenido exitosamente'
    };

  } catch (error) {
    console.error('❌ Error obteniendo pago semanal:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Resumen corto del pago semanal.
 * GET /api/credito-app/pago-semanal/resumen?codigoTipoCredito=CAS-001&fecha=2026-04-29
 * 
 * @param {string} codigoTipoCredito
 * @param {Date|string} fecha
 * @returns {Promise}
 */
export const obtenerPagoSemanalResumen = async (codigoTipoCredito, fecha) => {
  try {
    if (!codigoTipoCredito) {
      throw new Error('Código de tipo de crédito es requerido');
    }

    const fechaStr = formatFecha(fecha);
    if (!fechaStr) {
      throw new Error('Fecha es requerida');
    }

    console.log(`📋 Obteniendo resumen semanal ${codigoTipoCredito} para ${fechaStr}...`);

    const response = await apiFetch(
      `/api/credito-app/pago-semanal/resumen?codigoTipoCredito=${encodeURIComponent(codigoTipoCredito)}&fecha=${fechaStr}`
    );

    return {
      success: true,
      data: response.data || response,
      message: 'Resumen semanal obtenido exitosamente'
    };

  } catch (error) {
    console.error('❌ Error obteniendo resumen semanal:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Rango de semanas.
 * GET /api/credito-app/pago-semanal/rango?codigoTipoCredito=CAS-001&desde=2026-04-01&hasta=2026-06-30
 * 
 * @param {string} codigoTipoCredito
 * @param {Date|string} desde
 * @param {Date|string} hasta
 * @returns {Promise}
 */
export const obtenerPagoSemanalRango = async (codigoTipoCredito, desde, hasta) => {
  try {
    if (!codigoTipoCredito) {
      throw new Error('Código de tipo de crédito es requerido');
    }

    const desdeStr = formatFecha(desde);
    const hastaStr = formatFecha(hasta);

    if (!desdeStr || !hastaStr) {
      throw new Error('Fechas desde y hasta son requeridas');
    }

    console.log(`📅 Obteniendo rango semanal ${codigoTipoCredito} de ${desdeStr} a ${hastaStr}...`);

    const response = await apiFetch(
      `/api/credito-app/pago-semanal/rango?codigoTipoCredito=${encodeURIComponent(codigoTipoCredito)}&desde=${desdeStr}&hasta=${hastaStr}`
    );

    return {
      success: true,
      data: response.data || response,
      message: 'Rango semanal obtenido exitosamente'
    };

  } catch (error) {
    console.error('❌ Error obteniendo rango semanal:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Pagos pendientes hasta una fecha.
 * GET /api/credito-app/pago-semanal/pendientes?codigoTipoCredito=CAS-001&hasta=2026-06-30
 * 
 * @param {string} codigoTipoCredito
 * @param {Date|string} hasta
 * @returns {Promise}
 */
export const obtenerPagosPendientes = async (codigoTipoCredito, hasta) => {
  try {
    if (!codigoTipoCredito) {
      throw new Error('Código de tipo de crédito es requerido');
    }

    const hastaStr = formatFecha(hasta);
    if (!hastaStr) {
      throw new Error('Fecha hasta es requerida');
    }

    console.log(`⏳ Obteniendo pagos pendientes ${codigoTipoCredito} hasta ${hastaStr}...`);

    const response = await apiFetch(
      `/api/credito-app/pago-semanal/pendientes?codigoTipoCredito=${encodeURIComponent(codigoTipoCredito)}&hasta=${hastaStr}`
    );

    const data = Array.isArray(response) ? response : (response.data || []);

    return {
      success: true,
      data,
      count: data.length,
      message: 'Pagos pendientes obtenidos exitosamente'
    };

  } catch (error) {
    console.error('❌ Error obteniendo pagos pendientes:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

// ============================================================
//  FUNCIONES AUXILIARES (CLIENTE-SIDE)
// ============================================================

/**
 * Formatear fecha de miércoles a texto legible
 * @param {string|Date} fecha
 * @param {boolean} completo - Si incluir día de la semana
 * @returns {string}
 */
export const formatearFechaMiercoles = (fecha, completo = true) => {
  if (!fecha) return 'N/A';
  
  try {
    const fechaObj = new Date(fecha);
    const opciones = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    if (completo) {
      opciones.weekday = 'long';
    }
    
    return fechaObj.toLocaleDateString('es-VE', opciones);
  } catch (error) {
    return fecha;
  }
};

/**
 * Calcular el total de cuotas de un detalle semanal
 * @param {Object} pagoSemanal
 * @returns {number}
 */
export const contarCuotasTotales = (pagoSemanal) => {
  if (!pagoSemanal?.detalle) return 0;
  return pagoSemanal.detalle.reduce((sum, d) => sum + (d.cantidadCuotas || 0), 0);
};

/**
 * Agrupar el detalle por origen (Venta vs Reparación/Servicio)
 * @param {Object} pagoSemanal
 * @returns {Object}
 */
export const agruparDetallePorOrigen = (pagoSemanal) => {
  if (!pagoSemanal?.detalle) {
    return { ventas: [], reparaciones: [] };
  }
  
  const ventas = pagoSemanal.detalle.filter(d => 
    d.origen?.toLowerCase().includes('venta')
  );
  const reparaciones = pagoSemanal.detalle.filter(d => 
    !d.origen?.toLowerCase().includes('venta')
  );
  
  return { ventas, reparaciones };
};

// ============================================================
//  CONFIGURACIÓN POR DEFECTO
// ============================================================

/**
 * Código del tipo de crédito para Cashea (ajústalo si es distinto en tu BD)
 */
export const CODIGO_CASHEA = 'CAS-001';

// ============================================================
//  EXPORT DEFAULT
// ============================================================

export default {
  calcularMiercolesPago,
  obtenerProximoMiercoles,
  obtenerPagoSemanal,
  obtenerPagoSemanalResumen,
  obtenerPagoSemanalRango,
  obtenerPagosPendientes,
  formatearFechaMiercoles,
  contarCuotasTotales,
  agruparDetallePorOrigen,
  CODIGO_CASHEA
};